import { useOrganization, useUser } from '@clerk/clerk-react'
import { ArrowLeft, FileSignature, Pencil } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useTabOwnerId } from '@/components/layout/SheetContainerContext'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useAppTabs } from '@/hooks/useAppTabs'
import { useEditorDraft } from '@/hooks/useEditorDraft'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import { useWorkspaceCloseOwner } from '@/hooks/useWorkspaceCloseOwner'
import { DocumentPreview } from './DocumentPreview'
import { TemplatePreviewStatus } from './TemplatePreviewStatus'
import { FactsHelp } from './FactsHelp'
import { SigningFieldRow } from './SigningFieldRow'
import { SigningRow } from './SigningRow'
import { PreviewMemberPicker } from './PreviewMemberPicker'
import type { SelectedMember } from './PreviewMemberPicker'
import { useSigningFactsQuery, useSigningPreviewQuery } from './queries'
import { configurationIssues, effectiveSource, fieldIssue } from './model'
import type {
  SigningConfiguration,
  SigningField,
  SigningForm,
  TemplatePreview,
  SigningSlot,
} from './model'
import {
  configurationFromApi,
  formFromApi,
  useSigningConfigurationQuery,
  useSigningPersistence,
} from './persistence'
import type { CatalogForm, Prefill } from './persistence'

interface WorkspaceDraft {
  form?: SigningForm
  configuration?: SigningConfiguration
}

export function SigningConfigurationPage({
  formKey,
  configurationId,
}: {
  formKey: string
  configurationId?: string
}) {
  const { user } = useUser()
  const { organization } = useOrganization()
  const canManage = useIsCareforceOrgAdmin()
  const formQuery = useSigningConfigurationQuery<CatalogForm>(
    `forms/${encodeURIComponent(formKey)}`,
    true,
    true
  )
  const configurationQuery = useSigningConfigurationQuery<Prefill>(
    `prefills/${encodeURIComponent(configurationId ?? '')}`,
    Boolean(configurationId),
    true
  )
  const managesFields = canManage && !configurationId
  if (
    !user ||
    !organization ||
    formQuery.isPending ||
    (configurationId && configurationQuery.isPending)
  )
    return (
      <p className="p-6 text-sm text-muted-foreground">Loading document…</p>
    )
  if (formQuery.isError || (configurationId && configurationQuery.isError))
    return (
      <div role="alert" className="p-6 text-sm">
        Could not load this configuration.
        <Button
          variant="link"
          onClick={() => {
            void formQuery.refetch()
            if (configurationId) void configurationQuery.refetch()
          }}>
          Retry
        </Button>
      </div>
    )
  if (!formQuery.data) return <p className="p-6 text-sm">Document not found.</p>
  const form = formFromApi(formQuery.data)
  const configuration = configurationQuery.data
    ? configurationFromApi(configurationQuery.data)
    : undefined
  if (!managesFields && (!configuration || configuration.formKey !== formKey))
    return (
      <p className="p-6 text-sm">
        Choose a configuration from Document signing.
      </p>
    )
  if (!form.fields.length)
    return (
      <p className="p-6 text-sm text-muted-foreground">
        This template needs field annotations in David before it can be
        configured.
      </p>
    )
  return (
    <SigningWorkspace
      key={`${organization.id}:${form.key}:${configurationId ?? ''}`}
      form={form}
      configuration={managesFields ? undefined : configuration}
      managesFields={managesFields}
    />
  )
}

export function SigningWorkspace({
  form: savedForm,
  configuration: savedConfiguration,
  managesFields,
}: {
  form: SigningForm
  configuration?: SigningConfiguration
  managesFields: boolean
}) {
  const { toTask } = useAppNavigate()
  const persistence = useSigningPersistence()
  const { renameTab } = useAppTabs()
  const tabId = useTabOwnerId()
  const rowPrefix = useId()
  const fieldListRef = useRef<HTMLDivElement>(null)
  const [member, setMember] = useState<SelectedMember>()
  const catalogue = useSigningFactsQuery(savedForm.key)
  const signingFacts = catalogue.data?.facts ?? []
  const [selectedField, setSelectedField] = useState<string | null>(null)
  const [saveIssues, setSaveIssues] = useState<Record<string, string>>({})
  const [renaming, setRenaming] = useState(false)
  const [selectionOrigin, setSelectionOrigin] = useState<'document' | 'field'>(
    'document'
  )
  const [page, setPage] = useState(0)
  useEffect(() => setPage(0), [savedForm.revision])
  // Each workspace owns only its configuration or the Careforce form defaults.
  // Saving a tab must never write another tab's draft or an older form snapshot.
  const editor = useEditorDraft(
    JSON.stringify(
      managesFields
        ? { form: savedForm }
        : { configuration: savedConfiguration }
    ),
    {
      persist: { tabId: tabId ?? '', persistOnUnmount: true },
      successMessage: 'Document configuration saved',
      savedDraftFromResult: (result) => JSON.stringify(result),
      onSave: async (value) => {
        setSaveIssues({})
        const rejectField = (name: string, message: string): never => {
          setSaveIssues({ [name]: message })
          selectField(name, true)
          const field = form.fields.find((item) => item.name === name)
          throw new Error(`${field?.label ?? name}: ${message}`)
        }
        try {
          if (!catalogue.isSuccess)
            throw new Error('Load the Fact catalog before saving.')
          const next: WorkspaceDraft = JSON.parse(value)
          if (managesFields && next.form) {
            for (const field of next.form.fields.filter(
              (item) => item.ownership === 'template'
            )) {
              const issue = fieldIssue(field, field.source, signingFacts)
              if (issue) rejectField(field.name, issue)
            }
            return { form: await persistence.saveForm(next.form) }
          } else if (next.configuration) {
            if (!next.configuration.name.trim())
              throw new Error('Enter a configuration name.')
            const issues = configurationIssues(
              form,
              next.configuration,
              signingFacts,
              true
            )
            if (Object.keys(issues).length)
              rejectField(Object.keys(issues)[0]!, Object.values(issues)[0]!)
            if (!savedConfiguration)
              throw new Error('Reload this configuration before saving.')
            return {
              configuration: await persistence.saveConfiguration(
                form,
                next.configuration,
                savedConfiguration
              ),
            }
          }
        } catch (error) {
          if (
            error instanceof ApiError &&
            error.code === 'invalid_signing_fields'
          ) {
            const entries = error.details?.issues
            if (Array.isArray(entries)) {
              const problems: Record<string, string> = {}
              for (const entry of entries) {
                if (
                  entry &&
                  typeof entry.field === 'string' &&
                  typeof entry.message === 'string'
                )
                  problems[entry.field] = entry.message
              }
              setSaveIssues(problems)
              const first = Object.keys(problems)[0]
              if (first) {
                selectField(first, true)
                const field = form.fields.find((item) => item.name === first)
                throw new Error(
                  `${field?.label ?? first}: ${problems[first]}`,
                  { cause: error }
                )
              }
            }
          }
          throw error
        }
      },
    }
  )
  useWorkspaceCloseOwner({ save: editor.save })
  const draft: WorkspaceDraft = JSON.parse(editor.draft)
  // The form query is the authority for current policy. The fact catalogue's
  // annotation snapshot may be older and must not mask another editor's save.
  const assets = useSigningConfigurationQuery<TemplatePreview>(
    `forms/${encodeURIComponent(savedForm.key)}/assets`,
    true,
    (data) =>
      !data || data.revision !== savedForm.revision || data.state === 'pending'
        ? 3_000
        : 120_000
  )
  const currentAssets =
    assets.data?.revision === savedForm.revision ? assets.data : undefined
  const sourceForm = managesFields ? (draft.form ?? savedForm) : savedForm
  const form: SigningForm = {
    ...sourceForm,
    pages: currentAssets?.pages ?? [],
    signingSlots: (currentAssets?.fields ?? []).flatMap(
      (field): SigningSlot[] =>
        field.type === 'signature' || field.type === 'date'
          ? [
              {
                ...field,
                type: field.type,
                label:
                  field.type === 'signature'
                    ? 'Member signature'
                    : 'Signing date',
              },
            ]
          : []
    ),
    fields: sourceForm.fields
      .map((field) => {
        const layout = currentAssets?.fields.find(
          (item) => item.name === field.name
        )
        return {
          ...field,
          page: layout?.page ?? -1,
          rect: layout?.rect ?? ([0, 0, 0, 0] as SigningField['rect']),
        }
      })
      .toSorted(
        (a, b) =>
          a.page - b.page ||
          (Math.max(a.rect[1], b.rect[1]) < Math.min(a.rect[3], b.rect[3])
            ? a.rect[0] - b.rect[0]
            : b.rect[3] - a.rect[3])
      ),
  }
  const configuration = managesFields ? undefined : draft.configuration
  const title = managesFields
    ? 'Careforce settings'
    : configuration?.name || 'Untitled configuration'
  useEffect(() => {
    if (tabId) renameTab(tabId, `${form.name} · ${title}`)
  }, [tabId, renameTab, form.name, title])
  const previewing = Boolean(member)
  const preview = useSigningPreviewQuery(
    form,
    configuration,
    managesFields,
    member?.id
  )
  const values = Object.fromEntries(
    form.fields.map((field) => {
      const source = effectiveSource(field, configuration)
      return [
        field.name,
        previewing
          ? preview.data?.values[field.name]
          : source?.kind === 'literal'
            ? source.value
            : source?.binding
              ? `{${source.binding}}`
              : undefined,
      ]
    })
  )
  const issues =
    configuration && catalogue.isSuccess
      ? configurationIssues(form, configuration, signingFacts, true)
      : {}
  const previewIssues = Object.fromEntries(
    (preview.data?.issues ?? [])
      .filter((issue) => issue.field)
      .map((issue) => [issue.field!, 'No value for this preview'])
  )
  const updateField = (next: SigningField) => {
    setSaveIssues((current) => {
      const copy = { ...current }
      delete copy[next.name]
      return copy
    })
    editor.onChange(
      JSON.stringify({
        form: {
          ...sourceForm,
          fields: sourceForm.fields.map((field) =>
            field.name === next.name ? next : field
          ),
        },
      })
    )
  }
  const updateConfiguration = (next: SigningConfiguration) => {
    setSaveIssues({})
    editor.onChange(JSON.stringify({ configuration: next }))
  }
  const slots = form.signingSlots ?? []
  const selectField = (name: string, scroll = false, toggle = false) => {
    if (toggle && selectedField === name) {
      setSelectedField(null)
      return
    }
    const field =
      form.fields.find((item) => item.name === name) ??
      slots.find((slot) => slot.type === name)
    if (!field) return
    setSelectedField(name)
    setSelectionOrigin(scroll ? 'document' : 'field')
    if (field.page >= 0) setPage(field.page)
    if (scroll)
      requestAnimationFrame(() =>
        fieldListRef.current
          ?.querySelector(`[data-field="${name}"]`)
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      )
  }

  return (
    <div className="@container flex h-full min-h-0 flex-col overflow-y-auto">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Back to tools"
            onClick={() => toTask('/tools')}>
            <ArrowLeft />
          </Button>
          <FileSignature className="size-5 shrink-0 text-muted-foreground" />
          <h1 className="shrink-0 text-lg font-semibold">{form.name}</h1>
          <span className="text-muted-foreground">·</span>
          {renaming && configuration ? (
            <Input
              autoFocus
              aria-label="Configuration name"
              className="h-8 min-w-24 max-w-80 flex-1 font-medium"
              value={configuration.name}
              onChange={(event) =>
                updateConfiguration({
                  ...configuration,
                  name: event.target.value,
                })
              }
              onBlur={() => setRenaming(false)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault()
                  setRenaming(false)
                }
              }}
            />
          ) : (
            <span
              className="min-w-0 truncate text-sm font-medium"
              title={title}>
              {title}
            </span>
          )}
          {configuration && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Rename configuration"
              disabled={previewing || editor.isSaving}
              onClick={() => setRenaming(true)}>
              <Pencil className="size-3.5" />
            </Button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <FactsHelp />
          <PreviewMemberPicker
            value={member}
            onChange={(next) => {
              setRenaming(false)
              setMember(next)
            }}
          />
          <Button
            variant="ghost"
            disabled={previewing || editor.isSaving || !editor.dirty}
            onClick={() => {
              setSaveIssues({})
              editor.reset()
              setRenaming(false)
            }}>
            Cancel
          </Button>
          <Button
            disabled={
              previewing ||
              !editor.dirty ||
              editor.isSaving ||
              !catalogue.isSuccess
            }
            onClick={() => void editor.save()}>
            {editor.isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </header>
      {previewing &&
        (preview.isPending ||
          preview.isError ||
          preview.data?.issues.some((issue) => issue.field === null)) && (
          <div
            role={preview.isError ? 'alert' : 'status'}
            className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
            {preview.isPending
              ? 'Loading preview…'
              : preview.isError
                ? 'Could not load preview.'
                : 'This form needs review before signing.'}
            {preview.isError && (
              <Button
                variant="link"
                size="sm"
                onClick={() => void preview.refetch()}>
                Retry
              </Button>
            )}
          </div>
        )}
      {!previewing && !catalogue.isSuccess && (
        <div
          role={catalogue.isError ? 'alert' : 'status'}
          className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
          {catalogue.isError ? 'Could not load Facts.' : 'Loading Facts…'}
          {catalogue.isError && (
            <Button
              variant="link"
              size="sm"
              onClick={() => void catalogue.refetch()}>
              Retry
            </Button>
          )}
        </div>
      )}
      <div className="grid min-h-[360px] flex-1 grid-cols-1 overflow-auto @[880px]:grid-cols-[minmax(300px,0.9fr)_minmax(480px,1.1fr)] @[880px]:overflow-hidden">
        <div className="flex min-h-[460px] flex-col @[880px]:min-h-0">
          {!form.pages?.length ? (
            <TemplatePreviewStatus
              formKey={savedForm.key}
              state={currentAssets?.state}
              errorCode={currentAssets?.error_code}
              loading={
                assets.isPending ||
                (assets.data?.revision !== savedForm.revision &&
                  !assets.isError)
              }
              error={assets.isError}
              canRetry={managesFields}
              onReload={() => void assets.refetch()}
            />
          ) : (
            <DocumentPreview
              form={form}
              values={values}
              selectedField={selectedField}
              revealSelection={selectionOrigin === 'field'}
              onSelectField={(name) => selectField(name, true, true)}
              page={page}
              onPageChange={setPage}
            />
          )}
        </div>
        <TooltipProvider>
          <section
            aria-label="Document fields"
            className="@container/fields flex min-h-[420px] flex-col @[880px]:min-h-0">
            <fieldset disabled={editor.isSaving} className="contents">
              <div
                ref={fieldListRef}
                className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
                {form.fields.map((field) => (
                  <SigningFieldRow
                    key={field.name}
                    id={`${rowPrefix}-${field.name}`}
                    field={field}
                    configuration={configuration}
                    value={values[field.name]}
                    facts={signingFacts}
                    managesFields={managesFields}
                    previewing={previewing}
                    selected={selectedField === field.name}
                    issue={
                      previewing
                        ? previewIssues[field.name]
                        : (saveIssues[field.name] ??
                          (managesFields
                            ? field.ownership === 'template' &&
                              catalogue.isSuccess
                              ? fieldIssue(field, field.source, signingFacts)
                              : undefined
                            : issues[field.name]))
                    }
                    onSelect={(toggle) =>
                      selectField(field.name, false, toggle)
                    }
                    onUpdateField={updateField}
                    onUpdateConfiguration={updateConfiguration}
                  />
                ))}
                {slots.map((slot) => (
                  <SigningRow
                    key={slot.type}
                    id={`${rowPrefix}-${slot.type}`}
                    name={slot.type}
                    label={slot.label}
                    required
                    locked
                    previewing={previewing}
                    selected={selectedField === slot.type}
                    onSelect={(toggle) =>
                      selectField(slot.type, false, toggle)
                    }>
                    <div className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-1 text-sm">
                      <span className="px-2 text-xs text-muted-foreground">
                        {slot.type === 'signature' ? 'Signature' : 'Date'}
                      </span>
                      <span className="px-2 text-muted-foreground">
                        {slot.type === 'signature' ? 'Member' : 'When signed'}
                      </span>
                    </div>
                  </SigningRow>
                ))}
              </div>
            </fieldset>
          </section>
        </TooltipProvider>
      </div>
    </div>
  )
}
